/**
 * Ad Blocking Diagnostics Component
 *
 * Development-only component that displays real-time ad blocking status
 * to help debug why premium users might still see ads.
 *
 * Only visible in development mode.
 */
import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Chip, Alert, Divider, IconButton } from '@mui/material';
import { Minimize as MinimizeIcon, ExpandLess as ExpandIcon } from '@mui/icons-material';
import { useSubscription } from '../../hooks/useSubscription';
import { logger } from "../../utils/logger";
// import { logger } from "../../utils/logger";
export const AdBlockingDiagnostics: React.FC = () => {
    const { isAdFree, showAds, isLoading, premiumStatus, adEligibility, fetchSubscriptionStatus } = useSubscription();
    const [isMinimized, setIsMinimized] = useState(false);
    const [domState, setDomState] = useState({
        premiumAttribute: '',
        k9Attribute: '',
        adsenseBlockedAttribute: '',
        fundingChoicesBlockedAttribute: '',
        adScriptsFound: 0,
        adElementsFound: 0,
        adsbygoogleExists: false,
        googlefcExists: false
    });
    useEffect(() => {
        const updateDomState = () => {
            const html = document.documentElement;
            const premiumAttr = html.getAttribute('data-premium-user') || 'not set';
            const k9Attr = html.getAttribute('data-k9-user') || 'not set';
            const adsenseAttr = html.getAttribute('data-adsense-blocked') || 'not set';
            const fundingAttr = html.getAttribute('data-funding-choices-blocked') || 'not set';
            const adScripts = document.querySelectorAll('script[src*="googlesyndication"], script[src*="adsbygoogle"], script[src*="fundingchoicesmessages"]');
            const adElements = document.querySelectorAll('ins.adsbygoogle, [class*="adsbygoogle"], [id*="google_ads"], [id*="aswift_"], iframe[name="googlefcPresent"]');
            
            // Check if window.adsbygoogle is blocked (exists but is undefined with configurable:false)
            let adsbygoogleBlocked = false;
            try {
                const descriptor = Object.getOwnPropertyDescriptor(window, 'adsbygoogle');
                if (descriptor && descriptor.configurable === false && descriptor.value === undefined) {
                    adsbygoogleBlocked = true;
                }
            } catch (e) {
                // Ignore errors
            }
            
            setDomState({
                premiumAttribute: premiumAttr,
                k9Attribute: k9Attr,
                adsenseBlockedAttribute: adsenseAttr,
                fundingChoicesBlockedAttribute: fundingAttr,
                adScriptsFound: adScripts.length,
                adElementsFound: adElements.length,
                adsbygoogleExists: adsbygoogleBlocked || typeof (window as any).adsbygoogle !== 'undefined',
                googlefcExists: typeof (window as any).googlefc !== 'undefined'
            });
        };
        // Update immediately
        updateDomState();
        // Update periodically
        const interval = setInterval(updateDomState, 1000);
        return () => clearInterval(interval);
    }, []);
    // Only show in development
    if (process.env.NODE_ENV !== 'development') {
        return null;
    }
    const isServerAdFree =
        domState.premiumAttribute === 'true' ||
        domState.k9Attribute === 'true' ||
        domState.adsenseBlockedAttribute === 'true';
    const isBlocked = isAdFree || isServerAdFree;
    const hasIssues =
        (isAdFree && !isBlocked) ||
        (isBlocked && domState.adScriptsFound > 0) ||
        (isBlocked && domState.adElementsFound > 0);
    return (<Box sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 'auto',
            width: isMinimized ? 300 : 360,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: isMinimized ? 'auto' : '80vh',
            overflow: isMinimized ? 'visible' : 'auto',
            zIndex: 9999,
            boxShadow: 3
        }}>
      <Paper sx={{ p: isMinimized ? 1 : 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isMinimized ? 0 : 1 }}>
          <Typography variant={isMinimized ? "body2" : "h6"}>
            🐛 Ad Blocking Diagnostics
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={() => setIsMinimized(!isMinimized)} sx={{ p: 0.5 }}>
              {isMinimized ? <ExpandIcon fontSize="small"/> : <MinimizeIcon fontSize="small"/>}
            </IconButton>
            {!isMinimized && (<Chip label="🔄 Refresh" onClick={() => {
                logger.debug('🔄 Manually refreshing subscription status...');
                fetchSubscriptionStatus(true);
            }} sx={{ cursor: 'pointer' }} size="small" color="primary"/>)}
          </Box>
        </Box>

        {!isMinimized && (<>
            <Divider sx={{ my: 1 }}/>

        {/* Premium Status */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Premium Status
          </Typography>
          <Chip label={isAdFree ? '✅ Ad-free' : '❌ Ads enabled'} color={isAdFree ? 'success' : 'default'} size="small" sx={{ mr: 1 }}/>
          <Chip label={showAds ? 'showAds' : 'no ads'} size="small" sx={{ mr: 1 }}/>
          <Chip label={isLoading ? '⏳ Loading...' : '✅ Loaded'} color={isLoading ? 'warning' : 'success'} size="small"/>
          {premiumStatus && (<Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Source: {premiumStatus.source || 'none'}
              <br />
              Tier: {premiumStatus.subscriptionTier || 'none'}
              <br />
              Status: {premiumStatus.subscriptionStatus || 'none'}
            </Typography>)}
          {adEligibility && (<Typography variant="caption" display="block" sx={{ mt: 1 }}>
              adEligibility.showAds: {String(adEligibility.showAds)}
              <br />
              reasons: {(adEligibility.reasons || []).join(', ') || 'none'}
            </Typography>)}
        </Box>

        {/* Server-Side Attributes */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Server-Side Attributes
          </Typography>
          <Typography variant="caption" display="block">
            data-premium-user: <strong>{domState.premiumAttribute}</strong>
          </Typography>
          <Typography variant="caption" display="block">
            data-k9-user: <strong>{domState.k9Attribute}</strong>
          </Typography>
          <Typography variant="caption" display="block">
            data-adsense-blocked: <strong>{domState.adsenseBlockedAttribute}</strong>
          </Typography>
          <Typography variant="caption" display="block">
            data-funding-choices-blocked: <strong>{domState.fundingChoicesBlockedAttribute}</strong>
          </Typography>
        </Box>

        {/* Ad Detection */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Ad Detection
          </Typography>
          <Typography variant="caption" display="block">
            Ad Scripts Found: <strong>{domState.adScriptsFound}</strong>
            {domState.adScriptsFound === 0 && showAds && (
              <span style={{ color: 'orange' }}> ⚠️ (Script may not have loaded yet)</span>
            )}
          </Typography>
          <Typography variant="caption" display="block">
            Ad Elements Found: <strong>{domState.adElementsFound}</strong>
            {domState.adElementsFound === 0 && showAds && (
              <span style={{ color: 'orange' }}> ⚠️ (Ads may not have rendered yet)</span>
            )}
          </Typography>
          <Typography variant="caption" display="block">
            AdSense Script Status: <strong>
              {(() => {
                const script = document.getElementById('google-adsense-script');
                if (script) {
                  return '✅ Loaded';
                }
                const anyAdSenseScript = document.querySelector('script[src*="googlesyndication"]');
                if (anyAdSenseScript) {
                  return '✅ Found (different ID)';
                }
                if (showAds && !isLoading) {
                  return '❌ Not Found (should load for free users)';
                }
                return '⏳ Waiting...';
              })()}
            </strong>
          </Typography>
          <Typography variant="caption" display="block">
            window.adsbygoogle: <strong>
              {(() => {
                try {
                  const descriptor = Object.getOwnPropertyDescriptor(window, 'adsbygoogle');
                  if (descriptor && descriptor.configurable === false && descriptor.value === undefined) {
                    return '🚫 PERMANENTLY BLOCKED (clear cache)';
                  } else if (typeof (window as any).adsbygoogle !== 'undefined') {
                    return '✅ EXISTS';
                  } else {
                    return '✅ Available';
                  }
                } catch (e) {
                  return '❓ Unknown';
                }
              })()}
            </strong>
          </Typography>
          <Typography variant="caption" display="block">
            window.googlefc: <strong>{domState.googlefcExists ? '⚠️ EXISTS' : '✅ Blocked'}</strong>
          </Typography>
        </Box>

        {/* Status Alert */}
        {hasIssues && (<Alert severity="warning" sx={{ mt: 2 }}>
            ⚠️ Potential issues detected:
            {isAdFree && !isBlocked && (<Typography variant="caption" display="block">
                • Ad-free user but blocking attributes not set
              </Typography>)}
            {isBlocked && domState.adScriptsFound > 0 && (<Typography variant="caption" display="block">
                • {domState.adScriptsFound} ad script(s) found in DOM (should be blocked for premium users)
              </Typography>)}
            {isBlocked && domState.adElementsFound > 0 && (<Typography variant="caption" display="block">
                • {domState.adElementsFound} ad element(s) found in DOM (should be blocked for premium users)
              </Typography>)}
          </Alert>)}
          
        {/* Success message for free users with ads */}
        {!isBlocked && showAds && (domState.adScriptsFound > 0 || domState.adElementsFound > 0) && (
          <Alert severity="success" sx={{ mt: 2 }}>
            ✅ AdSense is working correctly for free user:
            {domState.adScriptsFound > 0 && (<Typography variant="caption" display="block">
                • {domState.adScriptsFound} ad script(s) loaded
              </Typography>)}
            {domState.adElementsFound > 0 && (<Typography variant="caption" display="block">
                • {domState.adElementsFound} ad element(s) rendered
              </Typography>)}
          </Alert>
        )}

            {!hasIssues && isBlocked && (<Alert severity="success" sx={{ mt: 2 }}>
                ✅ Ad blocking appears to be working correctly
              </Alert>)}
          </>)}
      </Paper>
    </Box>);
};
export default AdBlockingDiagnostics;
